import { createFileRoute, Outlet, useRouterState } from '@tanstack/react-router';
import { AuthGuard } from '../features/auth/auth-guard';
import { AuthSidebar } from '../features/layout/auth-sidebar';
import { AuthTopbar } from '../features/layout/auth-topbar';
import { MobileBottomNav } from '../features/layout/auth-mobile-nav';
import { NetworkStatusStrip } from '../features/layout/network-status-strip';
import { useNetworkStatus } from '../lib/offline/use-network-status';

export const Route = createFileRoute('/_auth')({
  component: AuthLayout,
});

/**
 * Editorial-brutalism auth layout — flat near-black canvas, no gradient blurs. Sidebar
 * lives on the left for the main app surfaces; the username setup page renders without
 * the sidebar so the user can focus on the single decision.
 */
function AuthLayout() {
  const path = useRouterState({ select: (s) => s.location.pathname });
  const isUsernameSetup = path === '/username';
  // Study mode = focused single-card view per mobile redesign spec §2. Hide
  // MobileBottomNav (collides with sticky CTA at fixed bottom-0 z-30) AND AuthTopbar
  // (user request: drop top login bar too — full "tylko karta" focus on every viewport).
  // System back gesture exits study and both nav surfaces reappear on /decks.
  const isFocusedRoute = path.startsWith('/study/') || path.startsWith('/review/');
  const { isOnline, pendingCount, syncStatus } = useNetworkStatus();

  // Strip visible iff NetworkStatusStrip would render (mirror logic from network-status-strip.tsx).
  // W trybie nauki strip jest ukryty (focused single-card view), więc nie wpływa na padding.
  const stripVisible =
    !isFocusedRoute && !(isOnline && pendingCount === 0 && syncStatus.kind === 'idle');

  if (isUsernameSetup) {
    return (
      <AuthGuard>
        <div className="min-h-dvh bg-[var(--color-bg-primary)] text-[var(--color-text-primary)]">
          <Outlet />
          <NetworkStatusStrip />
        </div>
      </AuthGuard>
    );
  }

  // pb compensation (mobile uwzględnia env(safe-area-inset-bottom) dla iOS PWA home indicator):
  //  - bez strip: 5rem (MobileBottomNav 64px) + safe-area, pb-0 desktop
  //  - ze strip: 6.25rem (MobileBottomNav 64px + strip 36px) + safe-area, pb-9 desktop
  //  - isFocusedRoute: brak MobileBottomNav, więc samo strip + safe-area mobile, pb-9 desktop
  //
  // pt: focused routes nie mają topbara, więc na PWA mobile treść wchodziłaby pod pasek
  // statusu telefonu (godzina/bateria). Dodajemy safe-area-inset-top tylko tam — non-focused
  // mają topbar z własnym handlowaniem safe-area. Na desktopie env zwraca 0 → no-op.
  const mainPadClass = isFocusedRoute
    ? stripVisible
      ? 'pt-[env(safe-area-inset-top)] pb-[calc(2.25rem+env(safe-area-inset-bottom))] md:pt-0 md:pb-9'
      : 'pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)] md:pt-0 md:pb-0'
    : stripVisible
      ? 'pb-[calc(6.25rem+env(safe-area-inset-bottom))] md:pb-9'
      : 'pb-[calc(5rem+env(safe-area-inset-bottom))] md:pb-0';

  return (
    <AuthGuard>
      <div className="relative flex min-h-dvh bg-[var(--color-bg-primary)] text-[var(--color-text-primary)]">
        <AuthSidebar />
        <div className="flex min-w-0 flex-1 flex-col">
          {!isFocusedRoute && <AuthTopbar />}
          <main
            data-focused-route={isFocusedRoute ? '' : undefined}
            className={`relative z-10 flex-1 overflow-auto ${mainPadClass}`}
          >
            <Outlet />
          </main>
        </div>
        {!isFocusedRoute && <MobileBottomNav />}
        {!isFocusedRoute && <NetworkStatusStrip />}
      </div>
    </AuthGuard>
  );
}
